import React from 'react';
import { PlayIcon, SparklesIcon } from 'lucide-react';
import { useAppState } from '../../contexts/AppStateContext';
import { useUi } from '../../contexts/UiContext';
import { TaskTemplate } from '../../types/family';
import { isDoneStatus } from '../../utils/progress';
import { opportunityOn } from '../../utils/rewards';
import { TodayItem } from '../../utils/today';
import { TaskCheckbox } from '../tasks/TaskCheckbox';
import { Button } from '../ui/Button';
interface GoodiesChancesCardProps {
  offers: TaskTemplate[];
  shared: TaskTemplate[];
  roomDay: TodayItem[];
}

/** Vse priložnosti za goodies na enem mestu: pomoč drugim, skupna skrb, urejena soba, dodatne minute. */
export function GoodiesChancesCard({
  offers,
  shared,
  roomDay
}: GoodiesChancesCardProps) {
  const {
    family,
    familyOcc,
    viewer,
    memberById,
    getOcc,
    takeOffer,
    declineOffer,
    markShared,
    myTemplates
  } = useAppState();
  const {
    requestTimerStart
  } = useUi();
  const r = family.rules;
  const extra = opportunityOn(r, 'extraMinutes') ? myTemplates.filter(t => t.extraMinutesBonus && t.targetMinutes && t.assigneeIds.includes(viewer.id)) : [];
  const openShared = shared.filter(t => {
    const o = getOcc(t.id);
    return !isDoneStatus(o.status) && o.status !== 'v_pregledu';
  });
  const doneShared = shared.filter(t => isDoneStatus(getOcc(t.id).status));
  if (offers.length + shared.length + roomDay.length + extra.length === 0) return null;
  const rowCls = 'flex flex-wrap items-center gap-x-3 gap-y-2 rounded-2xl bg-surface px-3 py-2.5';
  const value = (n: number) => <span className="shrink-0 rounded-full bg-bonus-soft px-2 py-0.5 text-xs font-extrabold text-bonus-ink" data-id="element-1180" data-mp="uc4fs401hi1u">+{n}</span>;
  return <section aria-labelledby="goodies-chances" className="rounded-3xl bg-bonus-soft p-4" data-id="element-1181" data-mp="kkqk67b7l2dz">
      <h2 id="goodies-chances" className="flex items-center gap-2 font-display text-lg text-bonus-ink" data-id="element-1182" data-mp="1hzw2m1j7hdhd">
        <SparklesIcon className="h-5 w-5" aria-hidden="true" data-id="element-1183" data-mp="1d84c1r01v7r8" /> Goodies priložnosti
      </h2>
      <p className="text-xs font-semibold text-bonus-ink" data-id="element-1184" data-mp="37fybqilscfkm">Dodatni trud = goodies za nagrade.</p>

      {offers.length > 0 && <div className="mt-3" data-id="element-1185" data-mp="3ce9lktc2yq2u">
          <h3 className="px-1 text-xs font-extrabold text-bonus-ink" data-id="element-1186" data-mp="3chnmdx53ir86">Lahko pomagaš?</h3>
          <ul className="mt-1.5 space-y-2" data-id="element-1187" data-mp="2xqesvcy2olpr">
            {offers.map(t => {
          const o = familyOcc[t.id];
          const personal = o?.offer?.toId === viewer.id;
          return <li key={t.id} className={rowCls} data-id="element-1188" data-mp="2os33fe8xztyw">
                  <span className="min-w-0 flex-1" data-id="element-1189" data-mp="2sru181wvzacx">
                    <span className="block text-sm font-bold" data-id="element-1190" data-mp="2sphdf84g65ry">{t.title}</span>
                    <span className="block text-xs text-muted" data-id="element-1191" data-mp="2etdnhr0lwjla">
                      {memberById(o?.offer?.byId)?.name} {personal ? 'prosi tebe' : 'prosi kogarkoli'} · samo danes
                    </span>
                  </span>
                  {t.baseValue ? value(t.baseValue) : null}
                  <span className="flex gap-1.5" data-id="element-1192" data-mp="hz0nh9jr58er">
                    {personal && <Button size="sm" variant="ghost" onClick={() => declineOffer(t.id)} data-id="element-1193" data-mp="22imi7o4xpai4">
                        Ne, hvala
                      </Button>}
                    <Button size="sm" variant="bonus" onClick={() => takeOffer(t.id)} data-id="element-1194" data-mp="2c7wap53owv6r">
                      Prevzamem
                    </Button>
                  </span>
                </li>;
        })}
          </ul>
        </div>}

      {shared.length > 0 && <div className="mt-3" data-id="element-1195" data-mp="2g9847lpoq0k8">
          <h3 className="px-1 text-xs font-extrabold text-bonus-ink" data-id="element-1196" data-mp="2g86imnb1lvo9">Kdo utegne? Skupna skrb</h3>
          <ul className="mt-1.5 space-y-2" data-id="element-1197" data-mp="2v5796nhhzkl5">
            {openShared.map(t => <li key={t.id} className={rowCls} data-id="element-1198" data-mp="2q6w1aiarjk5k">
                <span className="min-w-0 flex-1 text-sm font-bold" data-id="element-1199" data-mp="2q52sawcognue">{t.title}</span>
                {value(t.baseValue ?? 0)}
                <Button size="sm" variant="secondary" onClick={() => markShared(t.id)} data-id="element-1200" data-mp="vtebeomvyb7d">
                  Naredil(a) sem
                </Button>
              </li>)}
          </ul>
          {doneShared.length > 0 && <p className="mt-2 px-1 text-xs text-bonus-ink" data-id="element-1201" data-mp="3nzlya0aih0u6">
              Že narejeno:{' '}
              {doneShared.map(t => {
          const by = getOcc(t.id).performerId;
          return `${t.title} (${by === viewer.id ? 'ti' : memberById(by)?.name ?? 'nekdo'})`;
        }).join(', ')}
            </p>}
        </div>}

      {(roomDay.length > 0 || extra.length > 0) && <div className="mt-3" data-id="element-1202" data-mp="39bsioflfdybl">
          <h3 className="px-1 text-xs font-extrabold text-bonus-ink" data-id="element-1203" data-mp="39bip6i169915">Samo zate</h3>
          <ul className="mt-1.5 space-y-2" data-id="element-1204" data-mp="3e28yg6cpxw14">
            {roomDay.map(i => <li key={i.template.id} className="flex items-center gap-2 rounded-2xl bg-surface py-1 pl-1 pr-3" data-id="element-1205" data-mp="2hkkzlmu3q4jw">
                <TaskCheckbox template={i.template} status={i.occ.status} data-id="element-1206" data-mp="2lly8k5shqnv1" />
                <span className="min-w-0 flex-1 text-sm font-bold" data-id="element-1207" data-mp="2ljtrsn9htudc">{i.template.title}</span>
                {value(i.template.baseValue ?? r.roomBonus)}
              </li>)}
            {extra.map(t => <li key={t.id} className="flex items-center gap-2 rounded-2xl bg-surface py-1 pl-3 pr-1" data-id="element-1208" data-mp="251qppeqs1pg0">
                <span className="min-w-0 flex-1" data-id="element-1209" data-mp="2035yg4ti6g3p">
                  <span className="block text-sm font-bold" data-id="element-1210" data-mp="202migtney1sb">{t.title}: vsakih +{r.extraMinutesStep} min</span>
                  <span className="block text-xs text-muted" data-id="element-1211" data-mp="1v5w368jfna7y">nad {t.targetMinutes} min · največ +{r.extraDailyMax} na dan</span>
                </span>
                {value(r.extraMinutesBonus)}
                <button type="button" onClick={() => requestTimerStart({
            templateId: t.id
          })} aria-label={`Začni časovnik: ${t.title}`} className="grid h-11 w-11 shrink-0 place-items-center rounded-full text-bonus-ink transition-colors duration-150 hover:bg-bonus-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent" data-id="element-1212" data-mp="1le9jdnt142tl">
                  <PlayIcon className="h-4 w-4" fill="currentColor" aria-hidden="true" data-id="element-1213" data-mp="12pfwh4yx9p90" />
                </button>
              </li>)}
          </ul>
        </div>}
    </section>;
}